// Time mapping. One simulation tick is one real second, and one tick is one
// sim-second of real time — the game is real-time only (REFOCUS-2026-09.md §4:
// "Fast mode is cut"). The mode parameter and the 96× simulation factor went
// with it; nothing may compress biology to make the game shorter.

import type { WeatherState } from '../core/types';

/**
 * The only speed a player may run at. A first egg is ~19 weeks of hen age
 * whatever this is set to, because shrinking that would make the game teach
 * false numbers — only wall-clock time ever moved.
 *
 * The dev dashboard's speed buttons bypass this entirely by setting the tick
 * engine directly, so testers can compress time without a player ever seeing a
 * speed control.
 */
export const SPEED_PRESETS = [1] as const;
export const DEFAULT_SPEED = 1;

/** Speeds reachable from player UI. Today that is 1× and nothing else. */
export function allowedSpeeds(): readonly number[] {
  return SPEED_PRESETS;
}

/** Sim-hours one tick advances: one second of wall clock, honestly. */
export function simHoursPerTick(): number {
  return 1 / 3600;
}

/** Fractional hour-of-day in [0, 24) from an absolute sim-hour count. */
export function hourOfDay(simHour: number): number {
  return ((simHour % 24) + 24) % 24;
}

export function dayOf(simHour: number): number {
  return Math.floor(simHour / 24);
}

/**
 * Whether it is light out, according to the clock the player is looking at.
 *
 * `WeatherState.isDay` cannot answer this: it is the weather API's `is_day` for
 * the player's real location right now, and the sim clock runs on its own
 * schedule. Sunrise and sunset still come from the real forecast; only the
 * current time is the sim's.
 */
export function isDaylight(simHour: number, weather: WeatherState): boolean {
  const hour = hourOfDay(simHour);
  return hour >= weather.sunriseHour && hour < weather.sunsetHour;
}

/** Real-Time chore windows (GAME-DESIGN.md §5). */
export const MORNING_CHORE_HOUR = 7;
export const EVENING_CHORE_HOUR = 18.5;

export function formatClock(simHour: number, locale = 'en'): string {
  const h = hourOfDay(simHour);
  const hours = Math.floor(h);
  const minutes = Math.floor((h - hours) * 60);
  const date = new Date(2000, 0, 1, hours, minutes);
  return date.toLocaleTimeString(locale === 'es' ? 'es-PR' : 'en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Local calendar day key — chores refresh on real calendar boundaries. */
export function calendarDayKey(timestamp = Date.now()): string {
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}
